import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchWalletStatement,
  fetchWalletBalanceWithdrawals,
  fetchWalletStatementBySource,
} from "./thunks";

interface StatementItem {
  date: string;
  description: string;
  value: number;
  status: string;
}

interface WithdrawalItem {
  date: string;
  value: number;
  account: string | null;
  type: string;
  status: string;
}

interface StatementCards {
  total_entries: number;
  total_withdrawals: number;
  period_balance: number;
}

interface PaginationMeta {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

interface WalletListState<TCards, TItem> {
  loading: boolean;
  error: string | null;
  filters: any | null;
  cards: TCards | null;
  items: TItem[];
  pagination: PaginationMeta | null;
}

interface StatementBySourceEntry {
  payment_id?: string;
  date?: string;
  amount?: number;
  released_amount?: number | null;
  status?: string;
  release_date?: string | null;
  released_at?: string | null;
  description?: string;
  client_name?: string;
  plan_name?: string;
  payment_method?: string;
  gateway?: string;
}

interface PixWithdrawalItem {
  requested_at?: string;
  processed_at?: string | null;
  amount?: number;
  status?: string;
  transfer_type?: string;
  key_type?: string;
  key?: string;
}

interface StatementBySourceBlock<TItem> {
  items: TItem[];
  pagination: PaginationMeta | null;
}

interface StatementBySourceCards {
  asaas?: {
    gross_amount?: number | null;
    net_amount?: number | null;
    total_fees?: number | null;
  } | null;
  manual?: {
    total_amount?: number | null;
  } | null;
  pix?: {
    total_withdrawals?: number | null;
  } | null;
}

interface StatementBySourceState {
  loading: boolean;
  error: string | null;
  filters: Record<string, unknown> | null;
  cards: StatementBySourceCards | null;
  manualEntries: StatementBySourceBlock<StatementBySourceEntry>;
  asaasEntries: StatementBySourceBlock<StatementBySourceEntry>;
  pixWithdrawals: StatementBySourceBlock<PixWithdrawalItem>;
}

type WalletState = {
  statement: WalletListState<StatementCards, StatementItem>;
  balance: WalletListState<
    {
      saldo_disponivel: number;
      released_value: number;
      total_saques: number;
      recebido?: number;
      saldo_pendente?: number;
    },
    WithdrawalItem
  >;
  statementBySource: StatementBySourceState;
};

const baseListState = {
  loading: false,
  error: null,
  filters: null,
  cards: null,
  items: [],
  pagination: null,
};

const baseBlockState = {
  items: [] as any[],
  pagination: null as PaginationMeta | null,
};

const initialState: WalletState = {
  statement: { ...baseListState },
  balance: { ...baseListState },
  statementBySource: {
    loading: false,
    error: null,
    filters: null,
    cards: null,
    manualEntries: { ...baseBlockState },
    asaasEntries: { ...baseBlockState },
    pixWithdrawals: { ...baseBlockState },
  },
};

const subscriptionWalletSlice = createSlice({
  name: "subscriptionWallet",
  initialState,
  reducers: {
    clearWallet: (state) => {
      state.statement = { ...baseListState } as any;
      state.balance = { ...baseListState } as any;
      state.statementBySource = {
        loading: false,
        error: null,
        filters: null,
        cards: null,
        manualEntries: { ...baseBlockState },
        asaasEntries: { ...baseBlockState },
        pixWithdrawals: { ...baseBlockState },
      } as StatementBySourceState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Statement
      .addCase(fetchWalletStatement.pending, (state) => {
        state.statement.loading = true;
        state.statement.error = null;
      })
      .addCase(
        fetchWalletStatement.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.statement.loading = false;
          const payload = action.payload?.data || {};
          state.statement.filters = payload.filters || null;
          state.statement.cards = payload.cards || null;
          state.statement.items = payload.items || [];
          state.statement.pagination = payload.pagination || null;
        },
      )
      .addCase(fetchWalletStatement.rejected, (state, action) => {
        state.statement.loading = false;
        state.statement.error =
          (action.payload as any)?.error || "Falha ao carregar extrato";
      })
      // Balance & Withdrawals
      .addCase(fetchWalletBalanceWithdrawals.pending, (state) => {
        state.balance.loading = true;
        state.balance.error = null;
      })
      .addCase(
        fetchWalletBalanceWithdrawals.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.balance.loading = false;
          const payload = action.payload?.data || {};
          state.balance.filters = payload.filters || null;
          state.balance.cards = payload.cards || null;
          state.balance.items = payload.items || [];
          state.balance.pagination = payload.pagination || null;
        },
      )
      .addCase(fetchWalletBalanceWithdrawals.rejected, (state, action) => {
        state.balance.loading = false;
        state.balance.error =
          (action.payload as any)?.error || "Falha ao carregar saldo e saques";
      })
      // Statement by source
      .addCase(fetchWalletStatementBySource.pending, (state) => {
        state.statementBySource.loading = true;
        state.statementBySource.error = null;
      })
      .addCase(
        fetchWalletStatementBySource.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.statementBySource.loading = false;
          const payload = action.payload?.data || {};
          const manual = payload.manual_entries || {};
          const asaas = payload.asaas_entries || {};
          const pix = payload.pix_withdrawals || {};

          state.statementBySource.filters = payload.filters || null;
          state.statementBySource.cards = payload.cards || null;
          state.statementBySource.manualEntries = {
            items: Array.isArray(manual.items) ? manual.items : [],
            pagination: manual.pagination || null,
          };
          state.statementBySource.asaasEntries = {
            items: Array.isArray(asaas.items) ? asaas.items : [],
            pagination: asaas.pagination || null,
          };
          state.statementBySource.pixWithdrawals = {
            items: Array.isArray(pix.items) ? pix.items : [],
            pagination: pix.pagination || null,
          };
        },
      )
      .addCase(fetchWalletStatementBySource.rejected, (state, action) => {
        state.statementBySource.loading = false;
        state.statementBySource.error =
          (action.payload as any)?.error ||
          "Falha ao carregar extrato por origem";
      });
  },
});

export const { clearWallet } = subscriptionWalletSlice.actions;
export default subscriptionWalletSlice.reducer;
